import { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileAlt,
    faUsers,
    faChartBar,
    faCog,
    faBell,
    faBook,
    faChartLine,
    faBars,
    faTimes,
    faBlog,
    faUser,
    faCrown,
    faClock,
    faHome,
    faGem,
    faArrowLeft,
    faChevronLeft,
    faPlus,
    faSearch,
    faArrowRight,
    faGlobe,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import ApiServices from '../Services/ApiServices';
import Loader from '../Shared/Loader';
import ExamManager from './ExamManagement/ExamManager';
import StudentManager from '../Teacher/StudentManagement/StudentManager';
import Profile from './Profile';

import { useSearchParams, useNavigate } from 'react-router-dom';
import { onMessageListener } from '../../config/firebase';

import Reports from './Reports/Reports';
import CourseManager from './CourseManager/CourseManager';
import Sidebar from '../Shared/Sidebar';
import Settings from './Settings';
import BlogManager from './BlogManagement/BlogManager';
import SubscriptionViewer from './SubscriptionViewer';
import MozakaraManager from './MozakaraManagement/MozakaraManager';
import NotificationPanel from '../Shared/NotificationPanel';
import { useLanguage } from '../../contexts/LanguageContext';

const screenNameToIcon = {
    'ExamManager': faFileAlt,
    'StudentManager': faUsers,
    'Courses': faBook,
    'Blog': faBlog,
    'Reports': faChartLine,
    'Settings': faCog,
    'Profile': faUser,
    'Overview': faChartBar,
    'Subscription': faGem,
    'Mozakara': faBook,
};

const mapScreenNameToIcon = (screenName) => screenNameToIcon[screenName] || faChartBar;

const Dashboard = () => {
    // 1. Hooks (Must always be called in the same order)
    const { t, language, changeLanguage } = useLanguage();
    const [searchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [loading, setLoading] = useState(true);
    const [profileData, setProfileData] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [notificationCount, setNotificationCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);
    const [panelPosition, setPanelPosition] = useState({ top: 0, right: 0 });

    const notificationButtonRef = useRef(null);
    const contentRef = useRef(null);

    // 2. Effects
    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const response = await ApiServices.get('Dashboard/GetTeacherProfile');
                setProfileData(response.data);
            } catch (error) { } finally { setLoading(false); }
        };
        fetchProfileData();
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth > 768) setIsSidebarOpen(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const setupMessaging = async () => {
            try {
                const unsubscribe = await onMessageListener();
                if (unsubscribe) {
                    unsubscribe.then(payload => {
                        fetchNotifications();
                    }).catch();
                }
            } catch (error) { }
        };
        setupMessaging();
    }, []);

    useEffect(() => { fetchNotifications(); }, []);

    useEffect(() => {
        if (showNotifications && notificationButtonRef.current) {
            const rect = notificationButtonRef.current.getBoundingClientRect();

            if (isMobile) {
                const mobileTop = Math.max(12, rect.bottom + 8);
                const maxTop = Math.max(12, window.innerHeight - 420);
                const safeTop = Math.max(12, Math.min(mobileTop, maxTop));
                const maxPanelHeight = Math.max(220, window.innerHeight - safeTop - 12);

                setPanelPosition({
                    top: safeTop,
                    right: 12,
                    left: 12,
                    width: 'auto',
                    maxHeight: maxPanelHeight
                });
                return;
            }

            let rightPos = window.innerWidth - rect.right;

            // Prevent panel from going off-screen on the left
            if (window.innerWidth < 400) {
                // On small mobile, center it or pin to edges
                setPanelPosition({ top: rect.bottom + 10, right: 10, left: 10, width: 'calc(100vw - 20px)' });
            } else {
                // On desktop/tablet, ensure it has at least 10px from the left edge
                const panelWidth = 360;
                if (rect.right - panelWidth < 10) {
                    rightPos = window.innerWidth - (panelWidth + 10);
                }
                setPanelPosition({ top: rect.bottom + 10, right: Math.max(10, rightPos), width: 360 });
            }
        }
    }, [showNotifications, isMobile]);

    // 3. Logic & Handlers
    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    const getMenuItems = () => {
        const items = [];
        items.push({ id: 'overview', icon: faHome, label: t('dashboard') });
        if (profileData?.user_subscription_as_object?.plan_feature) {
            profileData.user_subscription_as_object.plan_feature.forEach(feature => {
                if (feature.included) {
                    let itemLabel = feature.name;
                    if (language === 'en') {
                        itemLabel = feature.screen_name.replace(/([A-Z])/g, ' $1').trim();
                    }
                    items.push({ id: feature.screen_name, icon: mapScreenNameToIcon(feature.screen_name), label: itemLabel });
                }
            });
        }
        items.push({ id: 'subscription', icon: faGem, label: t('subscription') });
        items.push({ id: 'profile', icon: faUser, label: t('profile') });
        items.push({ id: 'settings', icon: faCog, label: t('settings') });
        return items;
    };

    const handleTabChange = (tabId) => {
        if (tabId === 'home') { navigate('/'); }
        else {
            setActiveTab(tabId);
            const newUrl = `${window.location.pathname}?tab=${tabId}`;
            window.history.pushState({ path: newUrl }, '', newUrl);
        }
    };

    const fetchNotifications = async () => {
        try {
            const response = await ApiServices.get('Notification/getAll', { params: { PageSize: 10, PageNumber: 1, IsActive: true } });
            setNotifications(response.data.items || []);
            setNotificationCount(response.data.items.filter(n => !n.is_read).length);
        } catch (error) { }
    };

    const markAllAsRead = async () => {
        try {
            await ApiServices.post('Notification/markAllAsRead');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setNotificationCount(0);
        } catch (error) { }
    };

    const handleNotificationRead = (id) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        setNotificationCount(prev => Math.max(0, prev - 1));
    };

    // Calculate limit warnings
    const subscription = profileData?.user_subscription_as_object;
    const storageLimit = subscription?.storage_limit;
    const storageRemaining = subscription?.storage_remaining;
    const userLimit = subscription?.user_limit;
    const userRemaining = subscription?.user_remaining;

    const isStorageNearLimit = storageLimit && storageRemaining != null && (storageRemaining <= (storageLimit * 0.1));
    const isUserNearLimit = userLimit && userRemaining != null && (userRemaining <= (userLimit * 0.1));
    const showLimitHint = isStorageNearLimit || isUserNearLimit;

    // 4. Early Return (Must be after all top-level hooks)
    if (loading) return <div className="td-loading"><Loader /></div>;

    return (
        <div className="td-root" dir={language === 'ar' ? "rtl" : "ltr"}>
            <Sidebar
                isOpen={isSidebarOpen} onToggle={toggleSidebar}
                activeTab={activeTab} onTabChange={handleTabChange}
                menuItems={getMenuItems()} isMobile={isMobile}
                searchQuery={searchQuery}
                userProfile={profileData}
            />

            <main className={`td-main ${!isSidebarOpen ? 'is-collapsed' : ''}`}>
                {showLimitHint && (
                    <div className="td-limit-hint" style={{
                        background: '#fffbeb',
                        border: '1px solid #6366f1',
                        color: '#b45309',
                        padding: '12px 20px',
                        borderRadius: '12px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontWeight: '600'
                    }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span>
                            {t('limitWarning') || 'تنبيه: لقد اقتربت من استهلاك الحد الأقصى المسموح به '}
                            {isStorageNearLimit && isUserNearLimit
                                ? (t('forStorageAndStudents') || 'للتخزين والطلاب.')
                                : isStorageNearLimit
                                    ? (t('limitWarningforStorage') || t('forStorage') || 'للتخزين.')
                                    : (t('forStudents') || 'للطلاب.')}
                            {' '}
                            <button onClick={() => handleTabChange('subscription')} style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#d97706',
                                textDecoration: 'underline',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                padding: 0,
                                fontFamily: 'inherit'
                            }}>
                                {t('upgradeNow') || 'قم بالترقية الآن'}
                            </button>
                        </span>
                    </div>
                )}
                <header className="td-header">
                    <div className="td-header-left">
                        {isMobile && <button className="td-toggle" onClick={toggleSidebar}><FontAwesomeIcon icon={isSidebarOpen ? faTimes : faBars} /></button>}
                        <div className="td-welcome">
                            <h1>{t('welcome')}، {profileData?.teacher_name?.split(' ')[0]}</h1>
                            <p>{new Date().toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                        </div>
                    </div>

                    <div className="td-header-right">
                        <div className="td-search">
                            <FontAwesomeIcon icon={faSearch} />
                            <input
                                type="text"
                                placeholder={t('searchPlaceholder') || "ابحث في القائمة..."}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="td-actions" style={{ display: 'flex', gap: '8px' }}>
                            <button
                                ref={notificationButtonRef} className="td-icon-btn"
                                onClick={(e) => { e.stopPropagation(); setShowNotifications(!showNotifications); }}
                            >
                                <FontAwesomeIcon icon={faBell} />
                                {notificationCount > 0 && <span className="td-badge">{notificationCount}</span>}
                            </button>
                            <button
                                className="td-icon-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    changeLanguage(language === 'en' ? 'ar' : 'en');
                                }}
                                title={t('appLanguage')}
                            >
                                <FontAwesomeIcon icon={faGlobe} />
                                <span style={{ fontSize: '0.75rem', fontWeight: 'bold', marginLeft: '4px', marginRight: '4px' }}>
                                    {language === 'en' ? 'عربي' : 'EN'}
                                </span>
                            </button>

                            {showNotifications && ReactDOM.createPortal(
                                <NotificationPanel
                                    notifications={notifications}
                                    onClose={() => setShowNotifications(false)}
                                    panelStyle={{
                                        top: panelPosition.top,
                                        right: panelPosition.right,
                                        left: panelPosition.left,
                                        width: panelPosition.width,
                                        maxHeight: panelPosition.maxHeight,
                                    }}
                                    markAsReadAPI="Notification/markAsRead"
                                    getNotificationsAPI="Notification/getAll"
                                    useDecryptionAPI={true}
                                    pageSize={10}
                                    onNotificationRead={handleNotificationRead}
                                    onMarkAllRead={markAllAsRead}
                                    title={t('notifications')}
                                />,
                                document.body
                            )}
                        </div>
                    </div>
                </header>

                <div className="td-content" ref={contentRef}>
                    <AnimatePresence mode="wait">
                        {activeTab === 'overview' && (
                            <motion.div key="overview" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="td-overview">

                                <div className="td-banner">
                                    <div className="td-banner-info">
                                        <div className="tag"><FontAwesomeIcon icon={faCrown} /> {t('promoted') || 'بريميوم'}</div>
                                        <h2>{t('subscriptionType') || 'أنت مشترك في باقة'} {profileData?.user_subscription_as_object?.plan_name}</h2>
                                        <p>{t('remainingDays') || 'لديك يوم متبقي'}: {profileData?.user_subscription_as_object?.days_remaining}</p>
                                        <button onClick={() => handleTabChange('subscription')}>{t('viewDetails') || 'تجديد الاشتراك'} <FontAwesomeIcon icon={language === 'ar' ? faChevronLeft : faArrowRight} /></button>
                                    </div>
                                    <div className="td-banner-visual">
                                        <FontAwesomeIcon icon={faGem} />
                                    </div>
                                </div>

                                <div className="td-stats-grid">
                                    {[
                                        { label: t('activeExams'), value: profileData?.active_exams || 0, icon: faFileAlt, color: '#6366f1' },
                                        { label: t('totalStudents'), value: profileData?.student_count || 0, icon: faUsers, color: '#10b981' },
                                        { label: t('averageScore'), value: `${profileData?.average_score || 0}%`, icon: faChartLine, color: '#6366f1' },
                                        { label: t('features') || 'ساعات التدريس', value: '120+', icon: faClock, color: '#8b5cf6' }
                                    ].map((s, i) => (
                                        <div key={i} className="td-stat-card">
                                            <div className="icon" style={{ backgroundColor: `${s.color}15`, color: s.color }}><FontAwesomeIcon icon={s.icon} /></div>
                                            <div className="info">
                                                <span className="label">{s.label}</span>
                                                <span className="value">{s.value}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="td-split">
                                    <div className="td-panel">
                                        <div className="td-panel-head">
                                            <h3>{t('activity')}</h3>
                                            <button onClick={() => handleTabChange('Reports')}
                                                style={{ color: '#6366f1', fontFamily: 'zain,sans-serif' }}
                                            >{t('viewDetails') || 'عرض الكل'}</button>
                                        </div>
                                        <div className="td-timeline">
                                            {profileData?.last_exam_name ? (
                                                <div className="td-timeline-item">
                                                    <div className="dot" style={{ background: '#6366f1' }} />
                                                    <div className="body">
                                                        <p>{t('lastExam')}: <strong>{profileData.last_exam_name}</strong></p>
                                                        <small>{new Date(profileData.last_exam_date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}</small>
                                                    </div>
                                                </div>
                                            ) : <div className="td-empty">{t('noRecentExams')}</div>}
                                            {profileData?.last_completed_exam_name && profileData.last_completed_exam_name !== '-' && (
                                                <div className="td-timeline-item">
                                                    <div className="dot" style={{ background: '#10b981' }} />
                                                    <div className="body">
                                                        <p>{t('lastCompletedExam')}: <strong>{profileData.last_completed_exam_name}</strong></p>
                                                        <small>{new Date(profileData.last_completed_exam_date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US')}</small>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="td-panel">
                                        <div className="td-panel-head">
                                            <h3>{t('examUpcoming')}</h3>
                                            <button onClick={() => handleTabChange('ExamManager')}
                                                style={{ color: '#6366f1', fontFamily: 'zain,sans-serif' }}
                                            >{t('addExam')} <FontAwesomeIcon icon={faPlus} /></button>
                                        </div>
                                        <div className="td-exam-list">
                                            {profileData?.next_exams_as_list?.length > 0 ? profileData.next_exams_as_list.map((e, i) => {
                                                const d = new Date(e.date || e.exam_date || Date.now());
                                                return (
                                                    <div key={i} className="td-exam-item">
                                                        <div className="date">
                                                            <strong>{d.getDate()}</strong>
                                                            <span>{d.toLocaleString('ar-EG', { month: 'short' })}</span>
                                                        </div>
                                                        <div className="info">
                                                            <h4>{e.title || e.exam_title || t('examName') || 'اختبار تقييمي'}</h4>
                                                            <p>{e.student_count || 0} {t('students') || 'طالب'}</p>
                                                        </div>
                                                        <button className="go"><FontAwesomeIcon icon={language === 'ar' ? faArrowLeft : faArrowRight} /></button>
                                                    </div>
                                                );
                                            }) : <div className="td-empty">{t('noRecentExams') || 'لا توجد اختبارات مجدولة'}</div>}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {activeTab !== 'overview' && (
                            <motion.div key={activeTab} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="td-view">
                                {activeTab === 'ExamManager' && <ExamManager />}
                                {activeTab === 'StudentManager' && <StudentManager />}
                                {activeTab === 'Blog' && <BlogManager />}
                                {activeTab === 'Courses' && <CourseManager />}
                                {activeTab === 'Mozakara' && <MozakaraManager />}
                                {activeTab === 'Reports' && <Reports />}
                                {activeTab === 'subscription' && <SubscriptionViewer subscriptionData={profileData?.user_subscription_as_object} userAddons={profileData?.user_addons} />}
                                {activeTab === 'profile' && <Profile profileData={profileData} />}
                                {activeTab === 'settings' && <Settings />}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </main>

            <style jsx>{`
                .td-root { font-family: "Zain", sans-serif; background: white; min-height: 100vh; display: flex; color: #0f172a; }
                .td-main { flex: 1; min-width: 0; padding: 32px; transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1); position: relative; }
                .td-main::before 
                { 
                content: ""; position: absolute; top: 0; left: 0; right: 0; height: 300px; background: linear-gradient(180deg, rgba(99, 102, 241, 0.03) 0%, rgba(248, 250, 252, 0) 100%); pointer-events: none; 
                }
                .td-main.is-collapsed { padding-inline-end: 80px; }
                .td-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px; position: relative; z-index: 10; }
                .td-header-left { display: flex; align-items: center; gap: 20px; }
                .td-toggle { width: 44px; height: 44px; border: none; border-radius: 12px; background: rgba(255,255,255,0.8); backdrop-filter: blur(8px); color: #6366f1; cursor: pointer; box-shadow: 0 4px 12px rgba(0,0,0,0.03); transition: all 0.2s ease; }
                .td-toggle:hover { background: #fff; transform: scale(1.05); }
                .td-welcome h1 { font-size: 1.8rem; font-weight: 900; color: #0f172a; margin: 0; letter-spacing: -0.5px; }
                .td-welcome p { font-size: 1rem; color: #64748b; font-weight: 700; margin-top: 4px; }
                .td-header-right { display: flex; align-items: center; gap: 24px; background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(16px); padding: 8px 12px; border-radius: 25px; box-shadow: 0 4px 30px rgba(0,0,0,0.03); border: 1px solid rgba(255,255,255,0.4); }
                .td-search { position: relative; background: rgba(241, 245, 249, 0.8); border-radius: 50px; padding: 0 16px; display: flex; align-items: center; gap: 12px; width: 300px; height: 44px; transition: all 0.3s ease; border: 1px solid transparent; }
                .td-search:focus-within { background: #fff; border-color: #cbd5e1; box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1); }
                .td-search input { border: none; background: transparent; outline: none; font-family: inherit; font-weight: 700; width: 100%; color: #0f172a; }
                .td-search svg { color: #64748b; transition: 0.3s; }
                .td-search:focus-within svg { color: #6366f1; }
                .td-icon-btn { position: relative; width: 44px; height: 44px; border: none; background: transparent; color: #64748b; cursor: pointer; font-size: 1.2rem; transition: all 0.2s ease; border-radius: 50%; }
                .td-icon-btn:hover { background: rgba(99, 102, 241, 0.1); color: #6366f1; transform: translateY(-2px); }
                .td-badge { position: absolute; top: 6px; inset-inline-end: 6px; background: #ef4444; color: #fff; width: 18px; height: 18px; border-radius: 50%; font-size: 0.7rem; display: flex; align-items: center; justify-content: center; border: 2px solid #fff; box-shadow: 0 2px 5px rgba(239, 68, 68, 0.3); }
                .td-user { display: flex; align-items: center; gap: 12px; cursor: pointer; padding-inline-start: 8px; transition: all 0.2s ease; }
                .td-user:hover { opacity: 0.8; }
                .td-user img { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; border: 2px solid #fff; box-shadow: 0 4px 10px rgba(0,0,0,0.1); }
                .td-user .info { display: flex; flex-direction: column; }
                .td-user strong { font-size: 0.95rem; font-weight: 900; color: #0f172a; }
                .td-user span { font-size: 0.8rem; color: #64748b; font-weight: 700; }
                .td-banner { background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%); border-radius: 30px; padding: 48px; color: #fff; display: flex; justify-content: space-between; align-items: center; margin-bottom: 40px; position: relative; overflow: hidden; box-shadow: 0 20px 40px -15px rgba(124, 58, 237, 0.4); }
                .td-banner::before { content: ""; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: url('data:image/svg+xml;utf8,<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="2" fill="rgba(255,255,255,0.1)"/></svg>'); opacity: 0.5; }
                .td-banner-info { position: relative; z-index: 2; max-width: 600px; }
                .td-banner-info .tag { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.25); backdrop-filter: blur(10px); padding: 6px 16px; border-radius: 50px; font-weight: 900; font-size: 0.85rem; margin-bottom: 20px; border: 1px solid rgba(255,255,255,0.3); }
                .td-banner-info h2 { font-size: 2.2rem; font-weight: 900; margin-bottom: 12px; color: #ffffff; text-shadow: 0 2px 10px rgba(0,0,0,0.1); }
                .td-banner-info p { font-size: 1.1rem; opacity: 0.95; line-height: 1.6; font-weight: 500; margin-bottom: 32px; color: #ffffff; }
                .td-banner-info button { font-family: inherit; padding: 12px 32px; background: #ffffff; color: #6366f1; border: none; border-radius: 14px; font-weight: 900; cursor: pointer; display: flex; align-items: center; gap: 12px; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); box-shadow: 0 10px 20px rgba(0,0,0,0.1); }
                .td-banner-info button:hover { transform: translateY(-3px); box-shadow: 0 15px 25px rgba(0,0,0,0.15), 0 0 20px rgba(255,255,255,0.4); }
                .td-banner-visual { font-size: 16rem; opacity: 0.15; position: absolute; inset-inline-start: -20px; bottom: -50px; transform: rotate(15deg); filter: drop-shadow(0 20px 30px rgba(0,0,0,0.2)); transition: transform 0.5s ease; }
                .td-banner:hover .td-banner-visual { transform: rotate(10deg) scale(1.05); }
                .td-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; margin-bottom: 40px; }
                .td-stat-card { background: #ffffff; padding: 24px; border-radius: 24px; display: flex; align-items: center; gap: 20px; box-shadow: 0 10px 40px -10px rgba(0,0,0,0.06); transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); border: 1px solid rgba(226, 232, 240, 0.6); }
                .td-stat-card:hover { transform: translateY(-5px); box-shadow: 0 20px 40px -10px rgba(0,0,0,0.1); border-color: rgba(99, 102, 241, 0.2); }
                .td-stat-card .icon { width: 60px; height: 60px; border-radius: 18px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; transition: transform 0.3s ease; }
                .td-stat-card:hover .icon { transform: scale(1.1); }
                .td-stat-card .info { display: flex; flex-direction: column; }
                .td-stat-card .label { color: #64748b; font-weight: 700; font-size: 0.95rem; }
                .td-stat-card .value { font-size: 1.8rem; font-weight: 900; color: #0f172a; line-height: 1.2; }
                .td-split { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
                .td-panel { background: #ffffff; border-radius: 28px; padding: 32px; box-shadow: 0 10px 40px -10px rgba(0,0,0,0.06); border: 1px solid rgba(226, 232, 240, 0.6); }
                .td-panel-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px; font-family: 'Zain', sans-serif; }
                .td-panel-head h3 { font-size: 1.5rem; font-weight: 900; color: #0f172a; margin: 0; }
                .td-panel-head button { background: rgba(99, 102, 241, 0.1); border: none; color: #6366f1; padding: 8px 16px; border-radius: 10px; font-weight: 900; cursor: pointer; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 8px; }
                .td-panel-head button:hover { background: #6366f1; color: #ffffff; transform: translateY(-2px); }
                .td-timeline { display: flex; flex-direction: column; gap: 24px; position: relative; }
                .td-timeline-item { display: flex; gap: 20px; position: relative; transition: all 0.3s ease; padding: 8px 0; }
                .td-timeline-item:hover { transform: translateX(-4px); }
                .td-timeline-item::after { content: ''; position: absolute; right: 7px; top: 24px; bottom: -24px; width: 2px; background: #e2e8f0; }
                html[dir="ltr"] .td-timeline-item::after { right: auto; left: 7px; }
                html[dir="ltr"] .td-timeline-item:hover { transform: translateX(4px); }
                .td-timeline-item:last-child::after { display: none; }
                .td-timeline-item .dot { width: 16px; height: 16px; border-radius: 50%; border: 4px solid #fff; box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2); position: relative; z-index: 2; margin-top: 4px; transition: all 0.3s ease; }
                .td-timeline-item:hover .dot { box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.3); transform: scale(1.2); }
                .td-timeline-item .body p { font-size: 1.1rem; font-weight: 800; color: #0f172a; margin: 0 0 4px 0; }
                .td-timeline-item .body small { color: #64748b; font-weight: 700; font-size: 0.9rem; }
                .td-exam-list { display: flex; flex-direction: column; gap: 16px; }
                .td-exam-item { display: flex; align-items: center; gap: 20px; padding: 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 20px; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); cursor: pointer; }
                .td-exam-item:hover { border-color: #6366f1; background: #ffffff; box-shadow: 0 10px 25px -5px rgba(99, 102, 241, 0.15); transform: translateY(-3px); }
                .td-exam-item .date { background: #ffffff; width: 64px; height: 64px; border-radius: 16px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.05); border: 1px solid #f1f5f9; }
                .td-exam-item .date strong { font-size: 1.4rem; color: #0f172a; line-height: 1; }
                .td-exam-item .date span { font-size: 0.85rem; color: #64748b; font-weight: 900; text-transform: uppercase; }
                .td-exam-item .info { flex: 1; }
                .td-exam-item .info h4 { font-size: 1.15rem; font-weight: 900; color: #0f172a; margin: 0 0 6px; }
                .td-exam-item .info p { font-size: 0.9rem; color: #64748b; font-weight: 700; margin: 0; display: flex; align-items: center; gap: 6px; }
                .td-exam-item .go { width: 44px; height: 44px; border-radius: 14px; border: none; background: #f1f5f9; color: #64748b; cursor: pointer; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; }
                .td-exam-item:hover .go { background: #6366f1; color: #fff; transform: scale(1.1); box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4); }
                .td-loading { height: 100vh; display: flex; align-items: center; justify-content: center; background: #f8fafc; }
                .td-empty { padding: 32px; text-align: center; color: #64748b; font-weight: 800; font-size: 1.1rem; background: #f8fafc; border-radius: 16px; border: 1px dashed #cbd5e1; }
                @media (max-width: 1200px) { .td-stats-grid { grid-template-columns: repeat(2, 1fr); } .td-split { grid-template-columns: 1fr; } }
                @media (max-width: 768px) {
                    .td-main { padding: 16px; }
                    .td-header { flex-direction: column; align-items: stretch; gap: 20px; }
                    .td-header-right { flex-direction: column; width: 100%; border-radius: 24px; padding: 16px; background: #ffffff; }
                    .td-search { width: 100%; display: flex !important; flex-wrap: nowrap !important; align-items: center; }
                    .td-search svg { flex-shrink: 0; }
                    .td-search input { width: auto; flex: 1; min-width: 0; }
                    .td-banner { padding: 32px 24px; flex-direction: column; text-align: center; }
                    .td-banner-visual { display: none; }
                    .td-banner-info button { margin: 0 auto; }
                    .td-stats-grid { grid-template-columns: 1fr; }
                    .td-panel { padding: 24px 16px; }
                }
            `}</style>
        </div>
    );
};

export default Dashboard;